import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Anchor,
  Badge,
  Button,
  Code,
  Collapse,
  Container,
  CopyButton,
  Divider,
  Group,
  Modal,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useState } from "react"
import { BrandingFormView } from "../../../components/BrandingFormView"
import { DetailSkeleton } from "../../../components/PageSkeletons"
import {
  addCompanyMember,
  api,
  cancelCompanyInvitation,
  inviteCompanyMember,
  listCompanyInvitations,
  removeCompanyMember,
} from "../../../lib/api"
import { extractErrorMessage, unwrapData } from "../../../lib/api/errors"
import { authClient } from "../../../lib/auth-client"
import { notifyError } from "../../../lib/notify"
import { queryKeys } from "../../../lib/query-keys"
import { CompanyMembersList } from "./-company-detail/CompanyMembersList"

interface ICompanyData {
  id: string
  name: string
  slug: string
  logo: string | null
  metadata?: { brandColor?: string; loginBackground?: string; customCss?: string; defaultTheme?: string }
  domains?: Array<{
    domain: string
    verified: boolean
    status?: "pending" | "verified" | "failed"
    /** The domain generated links use when several are verified. */
    isPrimary?: boolean
    record?: { host: string; value: string }
  }>
  ssoProviders?: Array<{ id: string; providerId: string; issuer: string; domain: string }>
  members?: Array<{ id: string; role: string; createdAt: string; user?: { name: string; email: string } }>
}

function CompanyDetailPage(): React.JSX.Element {
  const { id } = Route.useParams()
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [logo, setLogo] = useState("")
  const [brandColor, setBrandColor] = useState("#2766d9") // theming-violation: color-input default hex
  const [loginBg, setLoginBg] = useState("")
  // "" = no company default (members fall back to the global default, Pastel Calm).
  const [defaultTheme, setDefaultTheme] = useState<string>("")
  const [initialized, setInitialized] = useState(false)

  const [ssoProvider, setSsoProvider] = useState("")
  const [ssoIssuer, setSsoIssuer] = useState("")
  const [ssoClientId, setSsoClientId] = useState("")
  const [ssoClientSecret, setSsoClientSecret] = useState("")
  const [ssoDomain, setSsoDomain] = useState("")

  const [customDomain, setCustomDomain] = useState("")

  // The 5-column member table overflows on phones — swap to a stacked card
  // list below the small breakpoint.
  const isMobile = useMediaQuery("(max-width: 48em)", false)

  const [showAddMember, setShowAddMember] = useState(false)
  const [addMode, setAddMode] = useState<"invite" | "password">("invite")
  const [newMemberName, setNewMemberName] = useState("")
  const [newMemberEmail, setNewMemberEmail] = useState("")
  const [newMemberPassword, setNewMemberPassword] = useState("")
  const [newMemberRole, setNewMemberRole] = useState<"member" | "admin">("member")
  const [shredOpen, setShredOpen] = useState(false)
  const [shredConfirm, setShredConfirm] = useState("")
  const [viewingAs, setViewingAs] = useState(false)

  const { data: company, isLoading } = useQuery({
    queryKey: queryKeys.admin.company(id),
    // The admin company-detail endpoint returns Better Auth's opaque org payload
    // (t.Record on the wire), so narrow the unwrapped record to our ICompanyData.
    queryFn: async () => unwrapData(await api.api.admin.companies({ id }).get()) as unknown as ICompanyData,
    select(data) {
      if (!initialized) {
        setName(data.name ?? "")
        setSlug(data.slug ?? "")
        setLogo(data.logo ?? "")
        const meta = data.metadata
          ? typeof data.metadata === "string"
            ? (JSON.parse(data.metadata) as ICompanyData["metadata"])
            : data.metadata
          : {}
        setBrandColor(meta?.brandColor ?? "#2766d9") // theming-violation: color-input default hex
        setLoginBg(meta?.loginBackground ?? "")
        setDefaultTheme(typeof meta?.defaultTheme === "string" ? meta.defaultTheme : "")
        setInitialized(true)
      }
      return data
    },
  })

  const { mutate: saveBranding, isPending: saving } = useMutation({
    mutationFn: async () =>
      unwrapData(
        await api.api.admin.companies({ id }).put({
          name,
          slug,
          logo: logo || null,
          // customCss intentionally not sent — Phase 2 ships real white-label
          // (10-shade palette derivation), so the free-form textarea was
          // dropped from the form. Existing rows keep their stored value but
          // applyBranding ignores it at runtime. Re-introduce behind a feature
          // flag if a customer asks.
          metadata: {
            brandColor,
            loginBackground: loginBg || null,
            defaultTheme: defaultTheme || null,
          },
        }),
      ),
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Saved`),
        message: i18n._(msg`Company branding updated`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: addSSO } = useMutation({
    mutationFn: async () =>
      unwrapData(
        await api.api.admin.companies({ id }).sso.post({
          providerId: ssoProvider || `oidc-${slug}`,
          issuer: ssoIssuer,
          domain: ssoDomain,
          clientId: ssoClientId,
          clientSecret: ssoClientSecret,
        }),
      ),
    onSuccess: () => {
      setSsoIssuer("")
      setSsoClientId("")
      setSsoClientSecret("")
      setSsoDomain("")
      setSsoProvider("")
      notifications.show({
        title: i18n._(msg`SSO Added`),
        message: i18n._(msg`SSO provider configured`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: addDomainMutation } = useMutation({
    mutationFn: async () => unwrapData(await api.api.admin.companies({ id }).domain.post({ domain: customDomain })),
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Domain added`),
        message: i18n._(msg`Add the DNS TXT record shown below, then click Verify.`),
        color: "info",
        autoClose: false,
      })
      setCustomDomain("")
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: setPrimaryDomainMutation } = useMutation({
    mutationFn: async (domain: string) =>
      unwrapData(await api.api.admin.companies({ id }).domain.primary.post({ domain })),
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Primary domain set`),
        message: i18n._(msg`Invitation and password-reset links now use this domain.`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: removeDomainMutation } = useMutation({
    mutationFn: async (domain: string) =>
      unwrapData(await api.api.admin.companies({ id }).domain.remove.post({ domain })),
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Domain removed`),
        message: i18n._(msg`The workspace is no longer served on that domain.`),
        color: "info",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const confirmRemoveDomain = (domain: string): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Remove ${domain}?`),
      children: (
        <Text size="sm">
          {i18n._(
            msg`People will no longer be able to open the workspace on this domain. Its DNS records stay yours to clean up.`,
          )}
        </Text>
      ),
      labels: { confirm: i18n._(msg`Remove`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => removeDomainMutation(domain),
    })
  }

  const handleVerifyDomain = async (domain: string): Promise<void> => {
    try {
      const result = unwrapData(await api.api.admin.companies({ id }).domain.verify.post({ domain }))
      if (result.verified) {
        notifications.show({
          title: i18n._(msg`Verified`),
          message: i18n._(msg`${domain} is now active`),
          color: "success",
        })
      } else {
        notifications.show({
          title: i18n._(msg`Not verified yet`),
          message: result.message,
          color: "warning",
          autoClose: false,
        })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    } catch (err: unknown) {
      notifyError(err)
    }
  }

  const { mutate: addMember, isPending: addingMember } = useMutation({
    mutationFn: () =>
      addCompanyMember(id, {
        email: newMemberEmail,
        password: newMemberPassword,
        name: newMemberName,
        role: newMemberRole,
      }),
    onSuccess: () => {
      setNewMemberName("")
      setNewMemberEmail("")
      setNewMemberPassword("")
      setNewMemberRole("member")
      setShowAddMember(false)
      notifications.show({
        title: i18n._(msg`Member added`),
        message: i18n._(msg`${newMemberEmail} has been added`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { data: pendingInvites } = useQuery({
    queryKey: queryKeys.admin.companyInvites(id),
    queryFn: () => listCompanyInvitations(id),
  })

  const { mutate: inviteMember, isPending: inviting } = useMutation({
    mutationFn: () => inviteCompanyMember(id, { email: newMemberEmail, role: newMemberRole }),
    onSuccess: () => {
      const invited = newMemberEmail
      setNewMemberName("")
      setNewMemberEmail("")
      setNewMemberRole("member")
      setShowAddMember(false)
      notifications.show({
        title: i18n._(msg`Invitation sent`),
        message: i18n._(msg`${invited} has been invited`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.companyInvites(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: revokeInvite } = useMutation({
    mutationFn: (invitationId: string) => cancelCompanyInvitation(id, invitationId),
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Invitation revoked`),
        message: i18n._(msg`The invitation has been revoked`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.companyInvites(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: removeMember } = useMutation({
    mutationFn: (memberId: string) => removeCompanyMember(id, memberId),
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Member removed`),
        message: i18n._(msg`Member has been removed from the company`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: cryptoShred, isPending: shredding } = useMutation({
    mutationFn: async () =>
      unwrapData(await api.api.admin.companies({ id })["crypto-shred"].post({ confirm: shredConfirm })),
    onSuccess: (res) => {
      notifications.show({
        title: i18n._(msg`Encryption key destroyed`),
        message: i18n._(
          msg`Destroyed ${res.versionsDestroyed} key version(s). This organization's encrypted data is now unrecoverable on live systems.`,
        ),
        color: "danger",
      })
      setShredOpen(false)
      setShredConfirm("")
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.company(id) })
    },
    onError: (err) => notifyError(err),
  })

  const promptRemoveMember = (memberId: string, email?: string): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Remove ${email || "this member"}?`),
      children: (
        <Text size="sm">
          <Trans>They will lose access to this company. This cannot be undone.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Remove`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => removeMember(memberId),
    })
  }

  const handleViewAs = async (): Promise<void> => {
    setViewingAs(true)
    try {
      const result = await authClient.organization.setActive({ organizationId: id })
      if (result.error) throw result.error
      window.location.href = "/"
    } catch (err: unknown) {
      setViewingAs(false)
      notifications.show({
        color: "danger",
        title: i18n._(msg`Could not open this company`),
        message: extractErrorMessage(err),
      })
    }
  }

  if (isLoading) {
    return (
      <Container size="md">
        <DetailSkeleton />
      </Container>
    )
  }
  if (!company) {
    return (
      <Text c="danger" ta="center">
        <Trans>Company not found</Trans>
      </Text>
    )
  }

  return (
    <Container size="md">
      <Group justify="space-between" mb="lg">
        <Group>
          <Anchor component={Link} to="/companies" size="sm">
            &larr; <Trans>Companies</Trans>
          </Anchor>
          <Title order={2}>{company.name}</Title>
        </Group>
        <Group gap="sm">
          <Button
            renderRoot={(props) => <Link to="/companies/$id/diagnostics" params={{ id }} {...props} />}
            variant="subtle"
            size="sm"
          >
            <Trans>Diagnostics</Trans>
          </Button>
          <Button
            renderRoot={(props) => <Link to="/companies/$id/settings" params={{ id }} {...props} />}
            variant="subtle"
            size="sm"
          >
            <Trans>Settings</Trans>
          </Button>
          <Button
            renderRoot={(props) => <Link to="/companies/$id/speakers" params={{ id }} {...props} />}
            variant="subtle"
            size="sm"
          >
            <Trans>Speakers</Trans>
          </Button>
          <Button variant="light" loading={viewingAs} onClick={() => void handleViewAs()}>
            <Trans>View as company</Trans>
          </Button>
        </Group>
      </Group>

      <Stack gap="xl">
        {/* Branding */}
        <Paper p="lg" withBorder>
          <Title order={4} mb="md">
            <Trans>Branding</Trans>
          </Title>
          <BrandingFormView
            showIdentity
            name={name}
            slug={slug}
            logoUrl={logo}
            brandColor={brandColor}
            loginBackgroundUrl={loginBg}
            defaultTheme={defaultTheme}
            readOnly={false}
            saving={saving}
            showTheme
            onNameChange={setName}
            onSlugChange={setSlug}
            onLogoUrlChange={setLogo}
            onBrandColorChange={setBrandColor}
            onLoginBackgroundUrlChange={setLoginBg}
            onDefaultThemeChange={setDefaultTheme}
            onSave={() => saveBranding()}
          />
        </Paper>

        {/* Custom Domains */}
        <Paper p="lg" withBorder>
          <Title order={4} mb="md">
            <Trans>Custom domains</Trans>
          </Title>
          {(company.domains?.length ?? 0) > 0 && (
            <Stack gap="lg" mb="md">
              {company.domains?.map((d) => {
                const status = d.status ?? (d.verified ? "verified" : "pending")
                return (
                  <Stack key={d.domain} gap="xs">
                    <Group justify="space-between" wrap="nowrap">
                      <Text fw={600} style={{ wordBreak: "break-all" }}>
                        {d.domain}
                      </Text>
                      <Group gap="xs" wrap="nowrap">
                        {status === "verified" && (
                          <Badge color="success" variant="light">
                            <Trans>Verified</Trans>
                          </Badge>
                        )}
                        {status === "pending" && (
                          <Badge color="warning" variant="light">
                            <Trans>Pending</Trans>
                          </Badge>
                        )}
                        {status === "failed" && (
                          <Badge color="danger" variant="light">
                            <Trans>Failed</Trans>
                          </Badge>
                        )}
                        {status !== "verified" && (
                          <Button size="xs" variant="light" onClick={() => handleVerifyDomain(d.domain)}>
                            {status === "failed" ? <Trans>Re-verify</Trans> : <Trans>Verify</Trans>}
                          </Button>
                        )}
                        {d.isPrimary ? (
                          <Badge color="brand" variant="light">
                            <Trans>Primary</Trans>
                          </Badge>
                        ) : (
                          status === "verified" && (
                            <Button size="xs" variant="subtle" onClick={() => setPrimaryDomainMutation(d.domain)}>
                              <Trans>Make primary</Trans>
                            </Button>
                          )
                        )}
                        <Button size="xs" variant="subtle" color="danger" onClick={() => confirmRemoveDomain(d.domain)}>
                          <Trans>Remove</Trans>
                        </Button>
                      </Group>
                    </Group>
                    {status !== "verified" && d.record && (
                      <Stack gap={4}>
                        <Text size="xs" c="dimmed">
                          <Trans>
                            Add this DNS TXT record, then verify — DNS changes can take a few minutes to propagate:
                          </Trans>
                        </Text>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="xs" fw={600} w={44}>
                            <Trans>Host</Trans>
                          </Text>
                          <Code style={{ flex: 1, wordBreak: "break-all" }}>{d.record.host}</Code>
                          <CopyButton value={d.record.host}>
                            {({ copied, copy }) => (
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                color={copied ? "success" : "brand"}
                                onClick={copy}
                              >
                                {copied ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
                              </Button>
                            )}
                          </CopyButton>
                        </Group>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="xs" fw={600} w={44}>
                            <Trans>Value</Trans>
                          </Text>
                          <Code style={{ flex: 1, wordBreak: "break-all" }}>{d.record.value}</Code>
                          <CopyButton value={d.record.value}>
                            {({ copied, copy }) => (
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                color={copied ? "success" : "brand"}
                                onClick={copy}
                              >
                                {copied ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
                              </Button>
                            )}
                          </CopyButton>
                        </Group>
                      </Stack>
                    )}
                  </Stack>
                )
              })}
            </Stack>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              addDomainMutation()
            }}
          >
            <Group>
              <TextInput
                value={customDomain}
                onChange={(e) => setCustomDomain(e.currentTarget.value)}
                placeholder="meetings.acme.com"
                style={{ flex: 1 }}
              />
              <Button type="submit" variant="light">
                <Trans>Add domain</Trans>
              </Button>
            </Group>
          </form>
          <Text size="xs" c="dimmed" mt="sm">
            <Trans>
              Point the domain at this platform first (an A or CNAME record), then add it here, publish the TXT record
              and verify. Signing in and the API work on the domain as soon as it is verified; Claude keeps using the
              platform MCP address. The primary domain is the one links in emails use.
            </Trans>
          </Text>
        </Paper>

        {/* SSO */}
        <Paper p="lg" withBorder>
          <Title order={4} mb="md">
            <Trans>SSO provider</Trans>
          </Title>
          {(company.ssoProviders?.length ?? 0) > 0 && (
            <Stack gap="xs" mb="md">
              {company.ssoProviders?.map((p) => (
                <Group key={p.id} gap="md">
                  <Badge variant="light">{p.providerId}</Badge>
                  <Text size="sm" c="dimmed">
                    {p.issuer}
                  </Text>
                  <Text size="sm" c="dimmed">
                    <Trans>Domain: {p.domain}</Trans>
                  </Text>
                </Group>
              ))}
              <Divider />
            </Stack>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              addSSO()
            }}
          >
            <Stack gap="sm">
              <Group grow>
                <TextInput
                  label={i18n._(msg`Provider ID`)}
                  value={ssoProvider}
                  onChange={(e) => setSsoProvider(e.currentTarget.value)}
                  placeholder={`oidc-${slug}`}
                />
                <TextInput
                  label={i18n._(msg`Email Domain`)}
                  value={ssoDomain}
                  onChange={(e) => setSsoDomain(e.currentTarget.value)}
                  placeholder="acme.com"
                  required
                />
              </Group>
              <TextInput
                label={i18n._(msg`Issuer URL`)}
                value={ssoIssuer}
                onChange={(e) => setSsoIssuer(e.currentTarget.value)}
                placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
                required
              />
              <Group grow>
                <TextInput
                  label={i18n._(msg`Client ID`)}
                  value={ssoClientId}
                  onChange={(e) => setSsoClientId(e.currentTarget.value)}
                  required
                />
                <PasswordInput
                  label={i18n._(msg`Client secret`)}
                  value={ssoClientSecret}
                  onChange={(e) => setSsoClientSecret(e.currentTarget.value)}
                  required
                />
              </Group>
              <Group>
                <Button type="submit">
                  <Trans>Add SSO provider</Trans>
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>

        {/* Members */}
        <Paper p="lg" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>
              <Trans>Members</Trans>
            </Title>
            <Button
              size="xs"
              variant={showAddMember ? "subtle" : "light"}
              onClick={() => setShowAddMember(!showAddMember)}
            >
              {showAddMember ? <Trans>Cancel</Trans> : <Trans>Invite member</Trans>}
            </Button>
          </Group>

          <Collapse in={showAddMember}>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (addMode === "invite") inviteMember()
                else addMember()
              }}
            >
              <Stack gap="sm" mb="md">
                <Text size="sm" c="dimmed">
                  {addMode === "invite" ? (
                    <Trans>They get an email and set their own password.</Trans>
                  ) : (
                    <Trans>Creates the account now with a password you choose.</Trans>
                  )}
                </Text>
                <Group grow>
                  <TextInput
                    label={i18n._(msg`Name`)}
                    value={newMemberName}
                    onChange={(e) => setNewMemberName(e.currentTarget.value)}
                    placeholder="John Doe"
                    required={addMode === "password"}
                  />
                  <TextInput
                    label={i18n._(msg`Email`)}
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.currentTarget.value)}
                    placeholder="john@company.com"
                    type="email"
                    required
                  />
                </Group>
                <Group grow>
                  {addMode === "password" ? (
                    <PasswordInput
                      label={i18n._(msg`Password`)}
                      value={newMemberPassword}
                      onChange={(e) => setNewMemberPassword(e.currentTarget.value)}
                      placeholder={i18n._(msg`Initial password`)}
                      required
                    />
                  ) : null}
                  <Select
                    label={i18n._(msg`Role`)}
                    value={newMemberRole}
                    onChange={(v) => {
                      if (v === "member" || v === "admin") setNewMemberRole(v)
                    }}
                    data={[
                      { value: "member", label: i18n._(msg`Member`) },
                      { value: "admin", label: i18n._(msg`Admin`) },
                    ]}
                  />
                </Group>
                <Group>
                  <Button type="submit" loading={addMode === "invite" ? inviting : addingMember}>
                    {addMode === "invite" ? <Trans>Send invitation</Trans> : <Trans>Add member</Trans>}
                  </Button>
                  <Button
                    type="button"
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => setAddMode(addMode === "invite" ? "password" : "invite")}
                  >
                    {addMode === "invite" ? (
                      <Trans>Create an account with a password instead</Trans>
                    ) : (
                      <Trans>Invite by email instead</Trans>
                    )}
                  </Button>
                </Group>
              </Stack>
              <Divider mb="md" />
            </form>
          </Collapse>

          <CompanyMembersList
            members={company.members ?? []}
            pendingInvites={pendingInvites ?? []}
            isMobile={isMobile}
            onRemoveMember={promptRemoveMember}
            onRevokeInvite={revokeInvite}
          />
        </Paper>

        {/* Danger zone — crypto-shred this org's encryption key (irreversible) */}
        <Paper p="lg" withBorder style={{ borderColor: "var(--mantine-color-danger-5)" }}>
          <Title order={4} mb="xs" c="danger">
            <Trans>Danger zone</Trans>
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            <Trans>
              Permanently destroy this organization's encryption key. All of its encrypted meeting content and audio
              become mathematically unrecoverable on live systems immediately. This cannot be undone.
            </Trans>
          </Text>
          <Button color="danger" variant="outline" onClick={() => setShredOpen(true)}>
            <Trans>Destroy encryption key (crypto-shred)</Trans>
          </Button>
        </Paper>
      </Stack>

      <Modal
        opened={shredOpen}
        onClose={() => setShredOpen(false)}
        title={i18n._(msg`Crypto-shred ${company.name}`)}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            <Trans>
              This destroys the encryption key for <strong>{company.name}</strong>. Every encrypted meeting, transcript,
              summary, and audio file for this organization becomes permanently unreadable on live systems, and
              unrecoverable from backups once the key is purged. There is no undo.
            </Trans>
          </Text>
          <Text size="sm" c="dimmed">
            <Trans>Type the organization name to confirm:</Trans> <strong>{company.name}</strong>
          </Text>
          <TextInput
            value={shredConfirm}
            onChange={(e) => setShredConfirm(e.currentTarget.value)}
            placeholder={company.name}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setShredOpen(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              color="danger"
              disabled={shredConfirm !== company.name}
              loading={shredding}
              onClick={() => cryptoShred()}
            >
              <Trans>Permanently destroy</Trans>
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}

export const Route = createFileRoute("/_auth/companies/$id")({
  component: CompanyDetailPage,
})
